import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Button, ListGroup, ListGroupItem } from "reactstrap";
import {
  formatFileSize,
  isImageFile,
  isPdfFile,
} from "@dhn/shared/utils/file-size";

/**
 * Lista de archivos adjuntos con tipo, nombre, tamaño y acciones.
 * Estilos en scss/dhn/_files.scss.
 *
 * items: [{ key, name, size, type, meta, previewUrl }]
 *
 * <FileList items={files} showSummary onRemove={(item) => remove(item.key)} />
 */
export default function FileList({
  actions,
  className,
  disabled,
  emptyText,
  grid,
  items,
  onPreview,
  onRemove,
  showSummary,
}) {
  if (!items.length) {
    return emptyText ? (
      <div className="dhn-file-list__empty">
        <i className="fa fa-file-text-o" aria-hidden="true" />
        <span>{emptyText}</span>
      </div>
    ) : null;
  }

  const totalSize = items.reduce((sum, item) => sum + Number(item.size || 0), 0);

  return (
    <ListGroup
      className={classnames("dhn-file-list", className, { "is-grid": grid })}
      aria-live="polite"
    >
      {showSummary && (
        <ListGroupItem className="dhn-file-list__summary">
          <span>
            <i className="fa fa-paperclip mr-2" aria-hidden="true" />
            {items.length} archivo{items.length === 1 ? "" : "s"}
          </span>
          <span className="d-flex align-items-center">
            {formatFileSize(totalSize, { empty: "0 KB" })}
            {actions}
          </span>
        </ListGroupItem>
      )}
      {items.map((item) => {
        const pdf = isPdfFile(item.type, item.name);
        const image = isImageFile(item.type, item.name);

        return (
          <ListGroupItem key={item.key} className="dhn-file-list__item">
            <span
              className={classnames("dhn-file-list__type", { "is-pdf": pdf })}
            >
              {item.previewUrl && image ? (
                <img src={item.previewUrl} alt={item.name} />
              ) : (
                <i
                  className={classnames("fa", {
                    "fa-file-pdf-o": pdf,
                    "fa-file-image-o": image,
                    "fa-file-o": !pdf && !image,
                  })}
                  aria-hidden="true"
                />
              )}
            </span>
            <span className="dhn-file-list__info">
              <strong title={item.name}>{item.name}</strong>
              <small>
                {item.meta ? `${item.meta} · ` : ""}
                {formatFileSize(item.size)}
              </small>
            </span>
            <span className="dhn-file-list__actions">
              {onPreview && (
                <Button
                  type="button"
                  color="info"
                  size="sm"
                  title={`Ver ${item.name}`}
                  aria-label={`Ver ${item.name}`}
                  onClick={() => onPreview(item)}
                >
                  <i className="fa fa-eye" aria-hidden="true" />
                </Button>
              )}
              {onRemove && (
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  disabled={disabled}
                  title={`Borrar ${item.name}`}
                  aria-label={`Borrar ${item.name}`}
                  onClick={() => onRemove(item)}
                >
                  <i className="fa fa-trash" aria-hidden="true" />
                </Button>
              )}
            </span>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
}

FileList.propTypes = {
  actions: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  emptyText: PropTypes.node,
  grid: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      meta: PropTypes.node,
      name: PropTypes.string,
      previewUrl: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    })
  ),
  onPreview: PropTypes.func,
  onRemove: PropTypes.func,
  showSummary: PropTypes.bool,
};

FileList.defaultProps = {
  disabled: false,
  grid: false,
  items: [],
  showSummary: false,
};
